import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { thermalPrinter } from '../native/thermalPrinter.js';
import { IcoPrint, IcoBluetooth, IcoCheck } from '../components/common/Icons.jsx';
import { usePrinterStore } from '../store/printerStore.js';
import { Capacitor } from '@capacitor/core';
import styles from './PrinterSetupScreen.module.css';

const isNative  = Capacitor.isNativePlatform();
const hasWebBT  = !isNative && 'bluetooth' in navigator;
const hasWebUSB = !isNative && 'usb' in navigator;

export default function PrinterSetupScreen() {
  const navigate        = useNavigate();
  const savedDeviceId   = usePrinterStore((s) => s.deviceId);
  const savedDeviceName = usePrinterStore((s) => s.deviceName);
  const savedTransport  = usePrinterStore((s) => s.transport);
  const setPrinter      = usePrinterStore((s) => s.setPrinter);
  const clearPrinter    = usePrinterStore((s) => s.clearPrinter);

  const [scanning,    setScanning]    = useState(false);
  const [devices,     setDevices]     = useState([]);
  const [connecting,  setConnecting]  = useState(null); // deviceId en proceso
  const [error,       setError]       = useState(null);
  const [success,     setSuccess]     = useState(null);
  const [autoPrint,   setAutoPrint]   = useState(() => localStorage.getItem('pos_auto_print') === 'true');
  const seen = useRef(new Set());

  const toggleAutoPrint = () => {
    const next = !autoPrint;
    setAutoPrint(next);
    localStorage.setItem('pos_auto_print', String(next));
  };

  const [connected, setConnected] = useState(() => thermalPrinter.isConnected());

  useEffect(() => {
    thermalPrinter.init().catch(() => {});
    // Reconecta en silencio si ya había una impresora autorizada
    thermalPrinter
      .autoReconnect(savedDeviceId, savedTransport)
      .then((t) => { if (t) setConnected(true); })
      .catch(() => {});
    return () => { thermalPrinter.stopScan().catch(() => {}); };
  }, []);

  const startScan = async () => {
    if (!isNative && !hasWebBT) {
      setError('El emparejamiento Bluetooth no está disponible en este navegador. Usa Chrome en Android o la app instalada.');
      return;
    }
    setError(null);
    setSuccess(null);
    setDevices([]);
    seen.current.clear();
    setScanning(true);

    try {
      await thermalPrinter.scan((device) => {
        if (!seen.current.has(device.deviceId)) {
          seen.current.add(device.deviceId);
          setDevices((prev) => [...prev, device]);
        }
      }, 8000);
    } catch (err) {
      // NotFoundError = usuario canceló el selector — no es un error real
      if (err.name !== 'NotFoundError') {
        setError('Error al escanear: ' + (err.message ?? 'Verifica que el Bluetooth esté activado'));
      }
    } finally {
      if (isNative) {
        // La app nativa sigue emitiendo dispositivos 8s tras el scan()
        setTimeout(() => setScanning(false), 8000);
      } else {
        setScanning(false);
      }
    }
  };

  const startScanUsb = async () => {
    setError(null);
    setSuccess(null);
    try {
      await thermalPrinter.scanUsb((device) => {
        setDevices([device]);
        seen.current.clear();
        seen.current.add(device.deviceId);
      });
    } catch (err) {
      // NotFoundError = usuario cerró el selector sin elegir
      if (err.name !== 'NotFoundError') {
        setError('Error al buscar por USB: ' + (err.message ?? 'Intenta de nuevo'));
      }
    }
  };

  const handleConnect = async (device) => {
    setError(null);
    setConnecting(device.deviceId);
    const transport = device.transport ?? (isNative ? 'native' : 'ble');
    const name      = device.name ?? device.deviceId;
    try {
      if (transport === 'usb') {
        await thermalPrinter.connectUsb(device.deviceId);
      } else {
        await thermalPrinter.connect(device.deviceId);
      }
      setPrinter(device.deviceId, name, transport);
      setConnected(true);
      setSuccess(`Impresora "${name}" conectada por ${transport === 'usb' ? 'USB' : 'Bluetooth'}.`);
    } catch (err) {
      setError('No se pudo conectar: ' + (err.message ?? 'Intenta de nuevo'));
    } finally {
      setConnecting(null);
    }
  };

  const handleClear = () => {
    thermalPrinter.disconnect().catch(() => {});
    clearPrinter();
    setConnected(false);
    setSuccess(null);
    setError(null);
  };

  return (
    <div className={styles.root}>
      <div className={styles.inner}>

        <div className={styles.header}>
          <button className="btn btn--ghost" onClick={() => navigate(-1)}>← Volver</button>
          <h1 className={styles.title}>Impresora de tickets</h1>
        </div>

        {/* Dispositivo emparejado */}
        {savedDeviceId && (
          <div className={styles.paired}>
            <div className={styles.pairedIcon}><IcoPrint size={28} /></div>
            <div className={styles.pairedInfo}>
              <p className={styles.pairedName}>{savedDeviceName ?? savedDeviceId}</p>
              <p className={styles.pairedSub}>
                {savedTransport === 'usb' ? 'USB' : 'Bluetooth'}
                {' · '}
                {connected ? 'Conectada' : 'Sin conexión — reconecta abajo'}
              </p>
            </div>
            <button className="btn btn--ghost" onClick={handleClear}>Quitar</button>
          </div>
        )}

        {/* Info web — sin ningún transporte disponible */}
        {!isNative && !hasWebBT && !hasWebUSB && (
          <div className={styles.webNote}>
            <p>Este navegador no permite conectar impresoras. Usa Chrome en Android o la app instalada en iOS/Android.</p>
            <p>Mientras tanto, el botón <strong>"Imprimir ticket"</strong> abre el diálogo de impresión del sistema.</p>
          </div>
        )}

        {/* Instrucciones */}
        {(isNative || hasWebBT || hasWebUSB) && (
          <div className={styles.instructions}>
            <p>1. Enciende la impresora — por USB, conéctala al dispositivo.</p>
            {isNative
              ? <p>2. Presiona <strong>Escanear</strong> — la impresora aparecerá en la lista.</p>
              : <p>2. Elige cómo está conectada — selecciónala en el diálogo del navegador.</p>
            }
            <p>3. Tócala para conectar. Solo se hace una vez.</p>
            {hasWebUSB && !isNative && (
              <p className={styles.tip}>
                Por USB la impresora queda autorizada de forma permanente: se
                reconecta sola al abrir la app. Por Bluetooth puede que tengas
                que volver a elegirla tras recargar.
              </p>
            )}
          </div>
        )}

        {/* Botones de conexión */}
        {(isNative || hasWebBT) && (
          <button
            className="btn btn--primary btn--full"
            onClick={startScan}
            disabled={scanning}
          >
            {scanning
              ? <><span className="spinner" /> {isNative ? 'Escaneando…' : 'Abriendo selector…'}</>
              : isNative ? 'Escanear dispositivos' : 'Buscar por Bluetooth'
            }
          </button>
        )}

        {hasWebUSB && !isNative && (
          <button
            className="btn btn--full"
            onClick={startScanUsb}
            disabled={scanning}
          >
            Buscar por USB
          </button>
        )}

        {/* Lista de dispositivos */}
        {devices.length > 0 && (
          <div className={styles.deviceList}>
            <p className={styles.listTitle}>Dispositivos encontrados</p>
            {devices.map((d) => (
              <button
                key={d.deviceId}
                className={styles.deviceRow}
                onClick={() => handleConnect(d)}
                disabled={!!connecting}
              >
                <span className={styles.deviceIcon}><IcoBluetooth size={16} /></span>
                <span className={styles.deviceName}>{d.name ?? 'Dispositivo desconocido'}</span>
                <span className={styles.deviceId}>{d.deviceId.slice(0, 8)}</span>
                {connecting === d.deviceId && <span className="spinner" />}
              </button>
            ))}
          </div>
        )}

        {scanning && devices.length === 0 && (
          <p className={styles.hint}>Buscando impresoras cercanas…</p>
        )}

        {error   && <p className={styles.error}>{error}</p>}
        {success && <p className={styles.successMsg}>{success}</p>}

        {/* Auto-print toggle */}
        <div className={styles.settingRow}>
          <div className={styles.settingInfo}>
            <p className={styles.settingLabel}>Auto-imprimir al cobrar</p>
            <p className={styles.settingHint}>Imprime el ticket automáticamente al completar una venta</p>
          </div>
          <button
            className={`${styles.toggle} ${autoPrint ? styles.toggleOn : ''}`}
            onClick={toggleAutoPrint}
            aria-pressed={autoPrint}
          >
            {autoPrint && <IcoCheck size={12} style={{ color: 'white' }} />}
          </button>
        </div>

      </div>
    </div>
  );
}
